import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ProfileImage from '../../../Components/WebChat/Common/ProfileImage';
import { getUserDetails, initialNameHandle } from '../../../Helpers/Chat/User';
import { formatActivityTime, toDateTime } from '../../../Components/WebChat/WebChatTimeStamp';
import { getEntrySummary } from './entrySummary';
import styles from './MessageEntryList.module.scss';

const NEXT_PAGE_MARGIN = '0px 0px 30% 0px';

/**
 * When a message was sent, as a number the lists can sort on.
 *
 * Reads the same pair the row renders, so the order on screen matches the times shown.
 * toDateTime absorbs the seconds, milliseconds and microseconds the SDK mixes.
 *
 * @param {object} message A starred or pinned message.
 * @returns {number} Milliseconds, or 0 when the message carries no usable time.
 */
export const sentTimeOf = message => {
    const time = toDateTime(message?.createdAt || message?.timestamp);
    return time?.isValid ? time.toMillis() : 0;
};

/**
 * Who sent a message, resolved the way the chat list resolves it.
 *
 * getUserDetails already answers "You" for the signed-in user, so the two lists agree on
 * the wording without a second check here.
 *
 * @param {object} message A starred or pinned message.
 * @returns {object} The sender's roster entry.
 */
const getSender = message => getUserDetails(message?.publisherId || message?.fromUserId || '');

const NextPageTrigger = ({ onReachEnd }) => {
    const triggerRef = useRef(null);

    useEffect(() => {
        const target = triggerRef.current;
        if (!target || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver(
            entries => entries.some(entry => entry.isIntersecting) && onReachEnd(),
            { rootMargin: NEXT_PAGE_MARGIN },
        );
        observer.observe(target);
        return () => observer.disconnect();
    }, [onReachEnd]);

    return <span ref={triggerRef} aria-hidden="true" />;
};

NextPageTrigger.propTypes = {
    onReachEnd: PropTypes.func.isRequired,
};

/**
 * A message list shared by the pinned, starred and kept screens.
 *
 * The screens differ only in the action a row offers, so the row itself lives here and each
 * screen passes its own icon, label and handler. canAct hides the action on a given row.
 *
 * @param {object} props The messages, the empty wording and the row action.
 * @returns {React.ReactElement} The list, or its empty state.
 */
const MessageEntryList = ({
    messages,
    emptyText,
    actionIcon: ActionIcon,
    actionLabel,
    onAction,
    onSelect,
    renderWho,
    onContextMenu,
    canAct,
    hasMore = false,
    onReachEnd,
}) => {
    if (!messages.length) {
        return <p className={styles.empty}>{emptyText}</p>;
    }

    return (
        <div className={styles.card}>
            {messages.map(message => {
                const sender = getSender(message);

                return (
                    <div
                        key={message.msgId}
                        className={styles.entry}
                        onClick={() => onSelect(message)}
                        onKeyDown={event => event.key === 'Enter' && onSelect(message)}
                        onContextMenu={onContextMenu ? event => onContextMenu(event, message) : undefined}
                        tabIndex="0"
                        role="button"
                    >
                        <span className={styles.avatar}>
                            <ProfileImage
                                chatType="chat"
                                temporary={false}
                                imageToken={sender.thumbImage || sender.image || ''}
                                name={initialNameHandle(sender)}
                                userId={message.publisherId}
                            />
                        </span>
                        <span className={styles.middle}>
                            <span className={styles.who}>
                                {renderWho ? renderWho(message, sender) : sender.displayName}
                            </span>
                            <span className={styles.text}>{getEntrySummary(message)}</span>
                        </span>
                        <span className={styles.right}>
                            <span className={styles.time}>
                                {formatActivityTime(message.createdAt || message.timestamp)}
                            </span>
                            {ActionIcon && (!canAct || canAct(message)) && (
                                <button
                                    type="button"
                                    className={styles.action}
                                    aria-label={actionLabel}
                                    title={actionLabel}
                                    onClick={event => {
                                        // The row itself jumps to the message; the action must not.
                                        event.stopPropagation();
                                        onAction(message);
                                    }}
                                >
                                    <ActionIcon />
                                </button>
                            )}
                        </span>
                    </div>
                );
            })}
            {/* Remounted per page: a sentinel still in view fires no new intersection. */}
            {hasMore && onReachEnd && <NextPageTrigger key={messages.length} onReachEnd={onReachEnd} />}
        </div>
    );
};

MessageEntryList.propTypes = {
    messages: PropTypes.array.isRequired,
    emptyText: PropTypes.string.isRequired,
    actionIcon: PropTypes.elementType,
    actionLabel: PropTypes.string,
    onAction: PropTypes.func,
    onSelect: PropTypes.func.isRequired,
    renderWho: PropTypes.func,
    onContextMenu: PropTypes.func,
    canAct: PropTypes.func,
    hasMore: PropTypes.bool,
    onReachEnd: PropTypes.func,
};

export default MessageEntryList;
